import React, { createContext, useContext, useEffect, useRef } from 'react'
import Lenis from 'lenis'
import { gsap } from 'gsap'

const LenisContext = createContext(null)

export function LenisProvider({ children }) {
  const lenisRef = useRef(null)

  useEffect(() => {
    // Pas de smooth scroll custom sur mobile/tactile : on garde le scroll natif,
    // plus confortable et plus performant sur ces appareils.
    const isFinePointer = window.matchMedia('(pointer: fine)').matches
    if (!isFinePointer) return

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    })
    lenisRef.current = lenis

    // Synchronise Lenis avec le ticker de GSAP pour que toutes les animations
    // (ScrollTrigger inclus) restent alignées.
    // NB: on NE désactive PAS le lag smoothing de GSAP ici (gsap.ticker.lagSmoothing(0)).
    // Ce réglage est parfois recommandé pour Lenis+ScrollTrigger, mais il désactive
    // l'amorti sur TOUTES les animations GSAP du site : un simple à-coup du thread
    // principal (changement de route, recréation de ScrollTrigger...) fait alors
    // "rattraper" instantanément les timelines en cours - typiquement le rideau
    // de transition de page qui se lève d'un coup au lieu de glisser.
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000)
    })

    return () => {
      lenis.destroy()
      lenisRef.current = null
    }
  }, [])

  const scrollTo = (target, options) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, { offset: -80, duration: 1.2, ...options })
    } else if (typeof target === 'string') {
      const el = document.querySelector(target)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <LenisContext.Provider value={{ scrollTo, lenisRef }}>
      {children}
    </LenisContext.Provider>
  )
}

export function useLenis() {
  return useContext(LenisContext)
}